import { CanvasOrientation } from "./types";

/**
 * Layout spec shared by the canvas preview (StampPreview) and PNG exporter
 * (downloadGraphic) so the two stay pixel-identical — tune values here and
 * both update together.
 *
 * Conventions:
 * - `canvas` is the native export size in px (stamp assets at 100% scale).
 * - `stamp` box is in % of the canvas; everything inside the stamp group
 *   (hackpal, accessory, effects, texts) is in % of the STAMP box, since
 *   those layers rotate with the stamp.
 * - The wave sits on the paper (canvas space), typically bottom-left.
 * - The ink stamp sits on top, unrotated, in % of the canvas.
 * - Each orientation has its own sizes/positions — bump landscape assets/
 *   text independently of square/portrait.
 *
 * WIP: layout tuning branch — adjust % / rotation / weight values below
 * against Figma for square / portrait / landscape. Preview + PNG export
 * both read from here.
 */

export interface Box {
  /** left, % */
  x: number;
  /** top, % */
  y: number;
  /** width, % */
  w: number;
}

/** Bottom-center anchored image box (keeps feet on the ground across pals) */
export interface AnchoredBox {
  /** center x, % */
  cx: number;
  /** bottom edge y, % */
  bottom: number;
  /** width, % */
  w: number;
}

/** Accessory overlay — same anchor as hackpal, plus its own tilt */
export interface AccessoryBox extends AnchoredBox {
  /** Extra rotation relative to the stamp group, degrees */
  rotationDeg: number;
}

export interface TextSpec {
  /** anchor x, % (left edge for `align: left`, center for `align: center`) */
  x: number;
  /** first line baseline-top y, % */
  y: number;
  /** max text width, % */
  maxW: number;
  /** font size as % of stamp width */
  fontPct: number;
  /** CSS/canvas font-weight (e.g. 700, 900) */
  fontWeight: number;
  /** line height as multiple of font size */
  lineHeight: number;
  align: "left" | "center";
  maxLines: number;
  /** Extra rotation relative to the stamp group, degrees */
  rotationDeg: number;
  /**
   * When single-line text exceeds maxW: shrink font, shift up, wrap.
   * Only applied to signage today.
   */
  overflow?: {
    /** Multiply fontPct when overflowing (e.g. 0.72) */
    fontScale: number;
    /** Add to y (% of stamp height; negative = up) */
    yShiftPct: number;
    /** Lines to wrap into (typically 2) */
    maxLines: number;
  };
}

export interface InkStampSpec {
  /** center x, % of canvas */
  cx: number;
  /** center y, % of canvas */
  cy: number;
  /**
   * Diameter as % of canvas width — primary ink-stamp size control.
   * Tune per orientation independently.
   */
  d: number;
  rotationDeg: number;
  /** Name arc overlay weight */
  nameFontWeight: number;
  /**
   * Shift the circular name down, as % of stamp diameter
   * (viewBox is 100 units across; ~1.5 ≈ 1–2px at typical preview scale).
   */
  nameYOffsetPct: number;
}

export interface OrientationLayout {
  canvas: { width: number; height: number };
  stampRotationDeg: number;
  stamp: Box;
  /** Wave overlay on the paper, bottom-left by default (% of canvas). */
  wave: Box;
  hackpal: AnchoredBox;
  accessory: AccessoryBox;
  effects: Box;
  headerText: TextSpec;
  signText: TextSpec;
  inkStamp: InkStampSpec;
}

export const LAYOUT: Record<CanvasOrientation, OrientationLayout> = {
  [CanvasOrientation.SQUARE]: {
    canvas: { width: 2160, height: 2160 },
    stampRotationDeg: 2.5,
    stamp: { x: 5.1, y: 5.1, w: 89.8 }, // 1940px native
    wave: { x: -5, y: 70, w: 28 },
    hackpal: { cx: 48.5, bottom: 69, w: 34 },
    accessory: { cx: 74, bottom: 65, w: 18, rotationDeg: 9 },
    effects: { x: 14, y: 35, w: 18 },
    headerText: {
      x: 13,
      y: 8.5,
      maxW: 80,
      fontPct: 7.5,
      fontWeight: 900,
      lineHeight: 1.2,
      align: "left",
      maxLines: 2,
      rotationDeg: 0,
    },
    signText: {
      x: 57,
      y: 76.5,
      maxW: 52,
      fontPct: 4.25,
      fontWeight: 300,
      lineHeight: 1.2,
      align: "center",
      maxLines: 1,
      rotationDeg: -6,
      overflow: { fontScale: 0.75, yShiftPct: -1.4, maxLines: 2 },
    },
    inkStamp: {
      cx: 87,
      cy: 21,
      d: 35,
      rotationDeg: -12,
      nameFontWeight: 700,
      nameYOffsetPct: 1.5,
    },
  },
  [CanvasOrientation.PORTRAIT]: {
    canvas: { width: 2228, height: 3925 },
    stampRotationDeg: 2.5,
    stamp: { x: 5.1, y: 5.1, w: 89.8 }, // 2000x3524 native
    wave: { x: -5, y: 65, w: 28 },
    hackpal: { cx: 50, bottom: 64.5, w: 47 },
    accessory: { cx: 79, bottom: 56, w: 18, rotationDeg: 9 },
    effects: { x: 10.5, y: 37, w: 22 },
    headerText: {
      x: 14,
      y: 8.5,
      maxW: 74,
      fontPct: 8.4,
      fontWeight: 900,
      lineHeight: 1.25,
      align: "left",
      maxLines: 2,
      rotationDeg: 0,
    },
    // NOTE: portrait signage has longer legs than square — sign board sits
    // slightly higher relative to the stamp bottom
    signText: {
      x: 54,
      y: 82.5,
      maxW: 66,
      fontPct: 4.6,
      fontWeight: 300,
      lineHeight: 1.2,
      align: "center",
      maxLines: 1,
      rotationDeg: -4,
      overflow: { fontScale: 0.8, yShiftPct: -1.25, maxLines: 2 },
    },
    inkStamp: {
      cx: 86,
      cy: 33,
      d: 38,
      rotationDeg: -6,
      nameFontWeight: 700,
      nameYOffsetPct: 1.5,
    },
  },
  [CanvasOrientation.LANDSCAPE]: {
    canvas: { width: 3925, height: 2228 },
    stampRotationDeg: 2.5,
    stamp: { x: 5.1, y: 5.1, w: 89.8 }, // 3524x2000 native
    // Landscape assets sized a bit larger than the previous pass
    wave: { x: -2, y: 66, w: 24 },
    hackpal: { cx: 48, bottom: 73, w: 23 },
    accessory: { cx: 66, bottom: 68, w: 13.5, rotationDeg: 9 },
    effects: { x: 22, y: 35, w: 15 },
    // Landscape: single centered headline line (own size/y vs square/portrait)
    headerText: {
      x: 50,
      y: 13,
      maxW: 84,
      fontPct: 5,
      fontWeight: 400,
      lineHeight: 1.25,
      align: "center",
      maxLines: 1,
      rotationDeg: 0,
    },
    signText: {
      x: 63,
      y: 75.2,
      maxW: 36,
      fontPct: 2.6,
      fontWeight: 300,
      lineHeight: 1.1,
      align: "center",
      maxLines: 1,
      rotationDeg: -6,
      overflow: { fontScale: 0.8, yShiftPct: -1.25, maxLines: 2 },
    },
    inkStamp: {
      cx: 90,
      cy: 42,
      d: 26,
      rotationDeg: -12,
      nameFontWeight: 700,
      nameYOffsetPct: 1.5,
    },
  },
};

/** Convert a percentage of `total` into px */
export const pct = (value: number, total: number): number =>
  (value / 100) * total;
